import { CustomErrorMessage } from '@src/design-system/error-message';
import { cn } from '@src/design-system/helpers';
import { inputVariants } from '@src/design-system/input';
import { Label } from '@src/design-system/label';

import { ErrorMessage, Form } from 'formik';
import { ChevronDown } from 'lucide-react';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@src/design-system/popover';
import type { TProgram } from '@shared/types/program';
import { ProgramsList } from '@src/shared/components/programs-list';
import FileUpload from '@src/design-system/molecules/file-upload';
import { Button } from '@src/design-system/button';
import { DocumentDownload } from 'iconsax-react';
import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { downloadImportTemplate } from '@src/shared/services/download-template';

export type TImportLeadsFormValues = {
  file: File | null;
  programs: TProgram[];
};

const ImportLeadsForm = ({
  values,
  setFieldValue,
}: {
  values: TImportLeadsFormValues;
  setFieldValue: (field: string, value: any) => void;
}) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleFileUpload = (file: File | null) => {
    setFieldValue('file', file);
  };

  const handleDownloadTemplate = async () => {
    setIsDownloading(true);
    try {
      await downloadImportTemplate('leads/download-import-template/', 'leads_import_template');
      toast.success('Template downloaded successfully');
    } catch (error) {
      toast.error('Failed to download template. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Form>
      <div className='mb-4'>
        <Label tabIndex={1}>Interested in</Label>
        <Popover>
          <PopoverTrigger
            className={cn(
              inputVariants(),
              'w-full cursor-pointer flex py-3 justify-between items-center font-normal disabled:opacity-70 text-gray-500 text-sm'
            )}
          >
            {values.programs?.length ? (
              <span className='text-gray-1000 flex flex-wrap gap-2'>
                {values.programs.slice(0, 3)?.map((p) => (
                  <span
                    key={p.id}
                    className='bg-primary-100 rounded-md py-1.5 max-w-[120px] px-2 truncate text-xs'
                    title={p.name}
                  >
                    {p.name}
                  </span>
                ))}
                {values.programs?.length > 3 && (
                  <span className='bg-primary-100 rounded-md py-1.5 max-w-[120px] px-2 truncate text-xs'>
                    +{values.programs?.length - 3}
                  </span>
                )}
              </span>
            ) : (
              'Select Programs'
            )}

            <ChevronDown
              size={16}
              className='shrink-0 self-center'
            />
          </PopoverTrigger>
          <PopoverContent
            withPortal={false}
            style={{
              width: 'var(--radix-popover-trigger-width)',
            }}
            className='p-0 z-50'
          >
            <ProgramsList
              props={{ infoItemProps: { withCheckbox: true } }}
              onClickItem={(program, isSelected) => {
                const programs = values.programs ?? [];
                const value = isSelected
                  ? programs?.filter((p) => p.id !== program.id)
                  : [...programs, program];
                setFieldValue('programs', value);
              }}
              isItemSelected={(program) => {
                return !!values.programs?.some((p) => p.id === program.id);
              }}
            />
          </PopoverContent>
        </Popover>

        <ErrorMessage
          name='programs'
          component={CustomErrorMessage}
        />
      </div>
      <div className='flex flex-col gap-2'>
        <div className='flex items-center justify-between'>
          <label className='text-gray-800 font-medium text-sm'>
            Upload Multiple Leads
          </label>
          <Button
            className='h-fit w-fit py-0.5 px-1'
            variant='ghost'
            onClick={handleDownloadTemplate}
            disabled={isDownloading}
          >
            <span className='text-primary-1000 font-medium text-sm underline -mr-1'>
              DOWNLOAD TEMPLATE
            </span>
            {isDownloading ? (
              <Loader2
                size={16}
                className='animate-spin text-primary-1000 w-4! h-4!'
              />
            ) : (
              <DocumentDownload className='text-primary-1000 w-4! h-4!' />
            )}
          </Button>
        </div>
        <FileUpload
          file={values.file as unknown as File}
          name='file'
          onChange={(file) => {
            handleFileUpload(file);
          }}
          allowedFormats='.xlsx, .xls, .xlsm, .csv'
          maxSize='800x400px'
          note='Note: You must download and use our template to import leads.'
        />
        <ErrorMessage
          name='file'
          component={CustomErrorMessage}
        />
      </div>
    </Form>
  );
};

export default ImportLeadsForm;
